import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Sparkles, Save, Trash2, UserCircle, X, Plus, PenLine, Check,
  MessageSquareText, Brain, Volume2, AlertTriangle, FileCode2, Wand2,
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { generateWritingProfile } from "@/services/contentEngine/writingProfileGenerator";
import { startLogRun, endLogRun, logPublic } from "@/services/contentEngine/contentLogger";
import { SocialProfilesEditor } from "./SocialProfilesEditor";
import { AUTHOR_PLATFORMS, slugify } from "@/services/schema/socialPlatforms";
import { suggestAuthorBio } from "@/services/schema/authorBioSuggester";

interface WritingProfilePanelProps {
  mapId: string;
  mapName?: string;
}

interface ProfileForm {
  author_name: string;
  public_author_label: string;
  author_identity: string;
  mission: string;
  audience_primary: string;
  voice_pronouns: string;
  verbosity: string;
  sentence_length: string;
  analogy_frequency: string;
  analogy_domain: string;
  claim_strength: string;
  evidence_style: string;
  glossary_mode: string;
  language_rules: string;
  preferred_phrases: string[];
  banned_phrases: string[];
  voice_characteristics: string[];
  content_personality: string;
  // Schema.org (publikacja — nie wpływa na Etap 1/2)
  schema_enabled: boolean;
  author_slug: string;
  author_photo_url: string;
  author_bio_html: string;
  author_job_title: string;
  author_url: string;
  author_same_as: string[];
  author_knows_about: string[];
}

const EMPTY_FORM: ProfileForm = {
  author_name: "", public_author_label: "", author_identity: "", mission: "", audience_primary: "",
  voice_pronouns: "ty", verbosity: "standard", sentence_length: "mixed", analogy_frequency: "moderate",
  analogy_domain: "", claim_strength: "moderate", evidence_style: "", glossary_mode: "first_mention",
  language_rules: "", preferred_phrases: [], banned_phrases: [], voice_characteristics: [], content_personality: "",
  schema_enabled: false,
  author_slug: "", author_photo_url: "", author_bio_html: "", author_job_title: "", author_url: "",
  author_same_as: [], author_knows_about: [],
};

const normalizeEnum = (val: unknown, allowed: string[], fallback: string): string =>
  typeof val === "string" && allowed.includes(val) ? val : fallback;

function SectionHeader({ icon: Icon, title, subtitle }: { icon: any; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border/50">
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{children}</label>;
}

function PhraseTagList({ phrases, field, onRemove, onAdd, placeholder, variant = "secondary" }: {
  phrases: string[]; field: string; onRemove: (i: number) => void; onAdd: (v: string) => void;
  placeholder: string; variant?: "secondary" | "destructive";
}) {
  const [input, setInput] = useState("");
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2 rounded-md border border-border/50 bg-muted/30 mb-2">
        {phrases.length === 0 && <span className="text-xs text-muted-foreground/50 italic">brak zwrotów</span>}
        {phrases.map((p, i) => (
          <Badge key={i} variant={variant} className="text-[11px] px-2 py-0.5 gap-1">
            {p}
            <button onClick={() => onRemove(i)} className="ml-0.5 opacity-60 hover:opacity-100"><X className="h-2.5 w-2.5" /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(input); setInput(""); } }}
          placeholder={placeholder} className="text-xs h-7"
        />
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { onAdd(input); setInput(""); }}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export function WritingProfilePanel({ mapId, mapName }: WritingProfilePanelProps) {
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Schema dual-layer opt-in: Person schema generuje się TYLKO gdy
  // website.schema_enabled=true AND profile.schema_enabled=true. Pokazujemy
  // warning gdy domena ma OFF (form pola niemodulowane, ale info dla user).
  const [websiteName, setWebsiteName] = useState<string | null>(null);
  const [websiteSchemaEnabled, setWebsiteSchemaEnabled] = useState<boolean>(false);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data: map } = await (supabase as any)
        .from("thematic_maps")
        .select("active_profile_id, website_id, website:websites(id, name, schema_enabled)")
        .eq("id", mapId)
        .single();
      const w = map?.website as { name?: string; schema_enabled?: boolean } | null;
      const websiteEnabledNow = Boolean(w?.schema_enabled);
      setWebsiteName(w?.name ?? null);
      setWebsiteSchemaEnabled(websiteEnabledNow);
      const { data: all } = await (supabase as any).from("thematic_writing_profiles").select("*").eq("map_id", mapId).order("created_at", { ascending: false });
      setProfiles(all || []);
      setActiveProfileId(map?.active_profile_id || null);
      if (map?.active_profile_id) {
        const active = (all || []).find((p: any) => p.id === map.active_profile_id);
        // FIX 2026-05-17: pass websiteEnabledNow explicitly — `setWebsiteSchemaEnabled` jest async,
        // bez tego loadFormFromProfile czytał stale closure (`websiteSchemaEnabled` z initial false)
        // i form.schema_enabled wymuszał false nawet gdy DB miało true → toggle wracał do OFF po reload.
        if (active) { setProfile(active); loadFormFromProfile(active, websiteEnabledNow); }
      }
    } catch {} finally { setLoading(false); }
  }, [mapId]);

  const loadFormFromProfile = (p: any, websiteEnabledOverride?: boolean) => {
    const wsOn = websiteEnabledOverride !== undefined ? websiteEnabledOverride : websiteSchemaEnabled;
    setForm({
      author_name: p.name || p.profile_name || "", public_author_label: p.public_author_label || "",
      author_identity: p.author_identity || "", mission: p.mission || "", audience_primary: p.audience_primary || "",
      voice_pronouns: normalizeEnum(p.voice_pronouns, ["ty", "wy", "pan_pani", "bezosobowy"], "ty"),
      verbosity: normalizeEnum(p.verbosity, ["concise", "standard", "detailed"], "standard"),
      sentence_length: normalizeEnum(p.sentence_length, ["short", "mixed", "long"], "mixed"),
      analogy_frequency: normalizeEnum(p.analogy_frequency, ["none", "moderate", "frequent"], "moderate"),
      analogy_domain: p.analogy_domain || "",
      claim_strength: normalizeEnum(p.claim_strength, ["cautious", "moderate", "bold"], "cautious"),
      evidence_style: p.evidence_style || "",
      glossary_mode: normalizeEnum(p.glossary_mode, ["first_mention", "always", "never"], "first_mention"),
      language_rules: p.language_rules || "", preferred_phrases: p.preferred_phrases || [], banned_phrases: p.banned_phrases || [],
      voice_characteristics: p.voice_characteristics || [], content_personality: p.content_personality || "",
      // Auto-sync z website: jeśli website ma schema OFF, profile też wymuszamy
      // na false (sekcja UI ukryta, brak sensu trzymać true który nic nie da).
      // wsOn = świeże website value (z arg lub state) — fix race condition po reload.
      schema_enabled: wsOn && Boolean(p.schema_enabled),
      author_slug: p.author_slug || "",
      author_photo_url: p.author_photo_url || "",
      author_bio_html: p.author_bio_html || "",
      author_job_title: p.author_job_title || "",
      author_url: p.author_url || "",
      author_same_as: Array.isArray(p.author_same_as) ? p.author_same_as : [],
      author_knows_about: Array.isArray(p.author_knows_about) ? p.author_knows_about : [],
    });
    setDirty(false);
  };

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const handleSelectProfile = async (id: string) => {
    await (supabase as any).from("thematic_maps").update({ active_profile_id: id }).eq("id", mapId);
    const sel = profiles.find((p) => p.id === id);
    if (sel) { setProfile(sel); setActiveProfileId(id); loadFormFromProfile(sel); toast({ title: "Profil aktywowany" }); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const startTime = Date.now();
    startLogRun(`profile-${mapId}`, { mapId, phase: "writing_profile_generation" });
    logPublic("PROFILE", "START — generowanie profilu pisarskiego");
    try {
      const d = await generateWritingProfile(mapId);
      setForm({
        author_name: (d.profile_name as string) || "", public_author_label: (d.public_author_label as string) || "",
        author_identity: (d.author_identity as string) || "", mission: (d.mission as string) || "",
        audience_primary: (d.audience_primary as string) || "",
        voice_pronouns: normalizeEnum(d.voice_pronouns, ["ty", "wy", "pan_pani", "bezosobowy"], "ty"),
        verbosity: normalizeEnum(d.verbosity, ["concise", "standard", "detailed"], "standard"),
        sentence_length: normalizeEnum(d.sentence_length, ["short", "mixed", "long"], "mixed"),
        analogy_frequency: normalizeEnum(d.analogy_frequency, ["none", "moderate", "frequent"], "moderate"),
        analogy_domain: (d.analogy_domain as string) || "",
        claim_strength: normalizeEnum(d.claim_strength, ["cautious", "moderate", "bold"], "cautious"),
        evidence_style: (d.evidence_style as string) || "",
        glossary_mode: normalizeEnum(d.glossary_mode, ["first_mention", "always", "never"], "first_mention"),
        language_rules: (d.language_rules as string) || "",
        preferred_phrases: Array.isArray(d.preferred_phrases) ? d.preferred_phrases as string[] : [],
        banned_phrases: Array.isArray(d.banned_phrases) ? d.banned_phrases as string[] : [],
        voice_characteristics: Array.isArray(d.voice_characteristics) ? d.voice_characteristics as string[] : [],
        content_personality: (d.content_personality as string) || "",
        // Schema.org pola (LLM ich nie generuje, ale form ich potrzebuje żeby
        // .length / .map nie crashował). Defaultowo puste, user uzupełnia ręcznie.
        schema_enabled: false,
        author_slug: "",
        author_photo_url: "",
        author_bio_html: "",
        author_job_title: "",
        author_url: "",
        author_same_as: [],
        author_knows_about: [],
      });
      setProfile(null); setDirty(true);
      const durationMs = Date.now() - startTime;
      logPublic("PROFILE", `DONE — ${(durationMs / 1000).toFixed(1)}s | author="${d.profile_name}" | voice=${d.voice_pronouns} | verbosity=${d.verbosity}`);
      endLogRun(`profile-${mapId}`, { durationMs, profileName: d.profile_name });
      toast({ title: "Profil wygenerowany", description: "Sprawdź i zapisz." });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Nieznany";
      logPublic("PROFILE", `ERROR: ${errorMsg}`);
      endLogRun(`profile-${mapId}`, { error: errorMsg });
      toast({ title: "Błąd", description: errorMsg, variant: "destructive" });
    } finally { setGenerating(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      if (!userId) throw new Error("Brak sesji");
      const payload: any = {
        user_id: userId, map_id: mapId,
        name: form.author_name || form.public_author_label || "Profil autora",
        profile_name: form.author_name || form.public_author_label,
        public_author_label: form.public_author_label, author_identity: form.author_identity,
        mission: form.mission, audience_primary: form.audience_primary,
        voice_pronouns: form.voice_pronouns, verbosity: form.verbosity,
        sentence_length: form.sentence_length, analogy_frequency: form.analogy_frequency,
        analogy_domain: form.analogy_domain, claim_strength: form.claim_strength,
        evidence_style: form.evidence_style, glossary_mode: form.glossary_mode,
        language_rules: form.language_rules, preferred_phrases: form.preferred_phrases,
        banned_phrases: form.banned_phrases, voice_characteristics: form.voice_characteristics,
        content_personality: form.content_personality, generated_from: profile?.generated_from || "auto_llm",
        schema_version: 1, language: "pl", is_active: true, version: 1,
        schema_enabled: form.schema_enabled,
        author_slug: form.author_slug || null,
        author_photo_url: form.author_photo_url || null,
        author_bio_html: form.author_bio_html || null,
        author_job_title: form.author_job_title || null,
        author_url: form.author_url || null,
        author_same_as: form.author_same_as,
        author_knows_about: form.author_knows_about,
      };
      if (profile?.id) {
        await (supabase as any).from("thematic_writing_profiles").update(payload).eq("id", profile.id);
        toast({ title: "Profil zapisany" });
      } else {
        const { data: ins, error } = await (supabase as any).from("thematic_writing_profiles").insert(payload).select().single();
        if (error) throw error;
        await (supabase as any).from("thematic_maps").update({ active_profile_id: ins.id }).eq("id", mapId);
        setProfile(ins); setActiveProfileId(ins.id);
        toast({ title: "Nowy profil utworzony" });
      }
      setDirty(false); await loadProfiles();
    } catch (err) {
      toast({ title: "Błąd", description: err instanceof Error ? err.message : "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!profile?.id) return;
    try {
      // Odłącz profil z mapy
      await (supabase as any).from("thematic_maps").update({ active_profile_id: null }).eq("id", mapId);
      // Usuń profil z DB
      await (supabase as any).from("thematic_writing_profiles").delete().eq("id", profile.id);
      setProfile(null); setActiveProfileId(null); setForm(EMPTY_FORM); setDirty(false);
      await loadProfiles();
      toast({ title: "Profil usunięty", description: "Profil został usunięty z bazy danych." });
    } catch (err) {
      toast({ title: "Błąd usuwania", description: err instanceof Error ? err.message : "", variant: "destructive" });
    }
  };

  const updateField = (f: keyof ProfileForm, v: string) => { setForm((p) => ({ ...p, [f]: v })); setDirty(true); };
  const updateArrayField = (f: "author_same_as" | "author_knows_about", v: string[]) => {
    setForm((p) => ({ ...p, [f]: v })); setDirty(true);
  };
  type PhraseField = "preferred_phrases" | "banned_phrases" | "voice_characteristics" | "author_knows_about";
  const addPhrase = (f: PhraseField, v: string) => { if (!v.trim()) return; setForm((p) => ({ ...p, [f]: [...p[f], v.trim()] })); setDirty(true); };
  const removePhrase = (f: PhraseField, i: number) => { setForm((p) => ({ ...p, [f]: p[f].filter((_, j) => j !== i) })); setDirty(true); };

  // AI Suggest bio
  const [suggestingBio, setSuggestingBio] = useState(false);
  const handleSuggestBio = async () => {
    if (!form.public_author_label.trim()) {
      toast({ title: "Brak danych", description: "Uzupełnij 'Imię i nazwisko' autora (Rola / perspektywa)", variant: "destructive" });
      return;
    }
    setSuggestingBio(true);
    try {
      // Pobierz macro_statement z mapy dla kontekstu
      const { data: map } = await (supabase as any)
        .from("thematic_maps").select("macro_statement").eq("id", mapId).single();
      const coreEntities = Array.isArray(profile?.core_entities) ? (profile.core_entities as string[]) : [];
      const bio = await suggestAuthorBio({
        publicAuthorLabel: form.public_author_label,
        authorJobTitle: form.author_job_title,
        mapMacroStatement: map?.macro_statement || "",
        coreEntities,
        knowsAbout: form.author_knows_about,
      });
      setForm((p) => ({ ...p, author_bio_html: bio }));
      setDirty(true);
      toast({ title: "Bio wygenerowane", description: `${bio.length} znaków — sprawdź i zapisz` });
    } catch (err) {
      toast({ title: "Błąd AI", description: err instanceof Error ? err.message : "", variant: "destructive" });
    } finally {
      setSuggestingBio(false);
    }
  };

  // Auto-slug on public_author_label change (tylko gdy author_slug pusty)
  useEffect(() => {
    if (form.public_author_label && !form.author_slug) {
      const auto = slugify(form.public_author_label);
      if (auto) setForm((p) => ({ ...p, author_slug: auto }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.public_author_label]);

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Ładowanie profilu...</div>;

  return (
    <div className="space-y-6">

      {/* ═══════ SELEKTOR + NOWY ═══════ */}
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
            <UserCircle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Profil autora</h3>
            <p className="text-xs text-muted-foreground">Wybierz istniejący profil lub wygeneruj nowy z danych mapy</p>
          </div>
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <FieldLabel>Aktywny profil</FieldLabel>
            <Select value={activeProfileId || "none"} onValueChange={(v) => v !== "none" && handleSelectProfile(v)}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Wybierz profil..." />
              </SelectTrigger>
              <SelectContent>
                {profiles.length === 0 && <SelectItem value="none" disabled>Brak profili — wygeneruj pierwszy</SelectItem>}
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      {p.name || p.public_author_label || "(bez nazwy)"}
                      {p.generated_from === "auto_llm" && <Badge variant="secondary" className="text-[9px] px-1 py-0">wygenerowany</Badge>}
                      {p.id === activeProfileId && <Check className="h-3 w-3 text-green-500" />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleGenerate} disabled={generating} size="default">
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {generating ? "Generuję..." : "Nowy profil (AI)"}
          </Button>
        </div>
      </div>

      {/* ═══════ FORMULARZ ═══════ */}
      {(profile || dirty) && (
        <>
          {/* Sticky action bar */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-lg border bg-card/95 backdrop-blur px-4 py-2.5">
            <div className="flex items-center gap-2">
              {profile?.generated_from === "auto_llm" && <Badge variant="secondary" className="text-[10px]">wygenerowany przez AI</Badge>}
              {profile?.model_used && <Badge variant="outline" className="text-[10px]">{profile.model_used}</Badge>}
              {dirty && <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30">niezapisane</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="h-8">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {saving ? "Zapisuję..." : "Zapisz zmiany"}
              </Button>
              {profile && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        Usunąć profil?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Profil <strong>"{profile.name || profile.public_author_label || "(bez nazwy)"}"</strong> zostanie
                        trwale usunięty z bazy danych. Tej operacji nie można cofnąć.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anuluj</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                        Usuń profil
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          {/* DANE AUTORA */}
          <div className="rounded-xl border bg-card p-5">
            <SectionHeader icon={PenLine} title="Dane autora" subtitle="Informacje widoczne na stronie" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>Imię i nazwisko</FieldLabel>
                <Input value={form.author_name} onChange={(e) => updateField("author_name", e.target.value)} placeholder="np. Jan Kowalski" className="h-10" />
              </div>
              <div>
                <FieldLabel>Publiczność docelowa</FieldLabel>
                <Input value={form.audience_primary} onChange={(e) => updateField("audience_primary", e.target.value)} placeholder="Dla kogo pisze?" className="h-10" />
              </div>
            </div>
            <div className="mt-4">
              <FieldLabel>Rola / perspektywa autora</FieldLabel>
              <Input value={form.public_author_label} onChange={(e) => updateField("public_author_label", e.target.value)} placeholder="np. Praktyk kuchni japońskiej z 10-letnim doświadczeniem" className="h-10" />
            </div>
            <div className="mt-4">
              <FieldLabel>Tożsamość — jak pisze, co go wyróżnia</FieldLabel>
              <Textarea value={form.author_identity} onChange={(e) => updateField("author_identity", e.target.value)} rows={3} className="resize-none" />
            </div>
            <div className="mt-4">
              <FieldLabel>Misja</FieldLabel>
              <Input value={form.mission} onChange={(e) => updateField("mission", e.target.value)} placeholder="Po co pisze? Jaki cel?" className="h-10" />
            </div>
          </div>

          {/* GŁOS */}
          <div className="rounded-xl border bg-card p-5">
            <SectionHeader icon={Volume2} title="Głos i styl" subtitle="Jak brzmi tekst autora" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <FieldLabel>Forma zwracania się</FieldLabel>
                <Select value={form.voice_pronouns} onValueChange={(v) => updateField("voice_pronouns", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ty">ty (nieformalny)</SelectItem>
                    <SelectItem value="wy">wy (plural)</SelectItem>
                    <SelectItem value="pan_pani">Pan/Pani</SelectItem>
                    <SelectItem value="bezosobowy">bezosobowy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Szczegółowość</FieldLabel>
                <Select value={form.verbosity} onValueChange={(v) => updateField("verbosity", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concise">zwięzły</SelectItem>
                    <SelectItem value="standard">standardowy</SelectItem>
                    <SelectItem value="detailed">szczegółowy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Długość zdań</FieldLabel>
                <Select value={form.sentence_length} onValueChange={(v) => updateField("sentence_length", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">krótkie</SelectItem>
                    <SelectItem value="mixed">mieszane</SelectItem>
                    <SelectItem value="long">długie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Analogie</FieldLabel>
                <Select value={form.analogy_frequency} onValueChange={(v) => updateField("analogy_frequency", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">brak</SelectItem>
                    <SelectItem value="moderate">umiarkowane</SelectItem>
                    <SelectItem value="frequent">częste</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <FieldLabel>Domena analogii</FieldLabel>
              <Input value={form.analogy_domain} onChange={(e) => updateField("analogy_domain", e.target.value)} placeholder="np. kuchnia, rzemiosło, sport" className="h-10" />
            </div>
          </div>

          {/* ARGUMENTACJA */}
          <div className="rounded-xl border bg-card p-5">
            <SectionHeader icon={Brain} title="Argumentacja" subtitle="Jak autor uzasadnia i buduje wiarygodność" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>Siła twierdzeń</FieldLabel>
                <Select value={form.claim_strength} onValueChange={(v) => updateField("claim_strength", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cautious">ostrożna</SelectItem>
                    <SelectItem value="moderate">umiarkowana</SelectItem>
                    <SelectItem value="bold">odważna</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Słownik terminów</FieldLabel>
                <Select value={form.glossary_mode} onValueChange={(v) => updateField("glossary_mode", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_mention">wyjaśniaj przy 1. użyciu</SelectItem>
                    <SelectItem value="always">zawsze wyjaśniaj</SelectItem>
                    <SelectItem value="never">nie wyjaśniaj</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <FieldLabel>Styl argumentacji</FieldLabel>
              <Textarea value={form.evidence_style} onChange={(e) => updateField("evidence_style", e.target.value)} rows={2} className="resize-none" placeholder="Jak autor uzasadnia swoje twierdzenia?" />
            </div>
            <div className="mt-4">
              <FieldLabel>Reguły językowe</FieldLabel>
              <Textarea value={form.language_rules} onChange={(e) => updateField("language_rules", e.target.value)} rows={2} className="resize-none" placeholder="Specyficzne reguły stylu, np. japońskie terminy przy pierwszym użyciu" />
            </div>
          </div>

          {/* VOICE DNA */}
          <div className="rounded-xl border bg-card p-5">
            <SectionHeader icon={Brain} title="Voice DNA" subtitle="Cechy głosu i osobowość autora" />
            <div className="space-y-4">
              <div>
                <FieldLabel>Cechy głosu autora ({form.voice_characteristics.length})</FieldLabel>
                <PhraseTagList
                  phrases={form.voice_characteristics} field="voice"
                  onRemove={(i) => removePhrase("voice_characteristics", i)}
                  onAdd={(v) => addPhrase("voice_characteristics", v)}
                  placeholder="Dodaj cechę + Enter" variant="secondary"
                />
              </div>
              <div>
                <FieldLabel>Osobowość treści</FieldLabel>
                <Textarea
                  value={form.content_personality}
                  onChange={(e) => updateField("content_personality", e.target.value)}
                  placeholder="2-3 zdania — co wyróżnia tego autora..."
                  rows={3} className="resize-none"
                />
              </div>
            </div>
          </div>

          {/* SCHEMA AUTORA (publikacja) — widoczna TYLKO gdy domena ma schema_enabled=true.
              Logika dual-layer opt-in (DOCS/modules/SCHEMA-ORG.md):
                - website.schema_enabled=false → cała sekcja UKRYTA + form.schema_enabled
                  zmuszony na false (auto-sync poniżej), bo nic z tych pól nie zostanie
                  użyte przy publish
                - website.schema_enabled=true → pełny box z togglem profilu */}
          {!websiteSchemaEnabled ? (
            <div className="rounded-xl border border-border/40 bg-muted/20 p-4 text-xs text-muted-foreground">
              <p className="leading-snug">
                <span className="text-foreground/70 font-semibold">
                  📕 Schema autora — niedostępna
                </span>
                <br />
                Domena <strong className="text-foreground/80">{websiteName ?? "—"}</strong>{" "}
                ma <code>schema_enabled=false</code>. Sekcja Schema.org Person ukryta —
                wpisy publikujemy tylko z BlogPosting + BreadcrumbList. Włącz Schema.org
                w ustawieniach domeny (<strong>Domeny → {websiteName ?? "domena"}</strong>),
                żeby ta sekcja się pojawiła.
              </p>
            </div>
          ) : (
          <div className={`rounded-xl border p-5 transition-colors ${form.schema_enabled ? "bg-indigo-500/5 border-indigo-500/40" : "bg-card"}`}>
            {/* Header z togglem */}
            <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-border/50">
              <div className="flex items-start gap-3 flex-1">
                <div className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${form.schema_enabled ? "bg-indigo-500/15" : "bg-muted"}`}>
                  <FileCode2 className={`h-4 w-4 ${form.schema_enabled ? "text-indigo-500" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold">Schema autora (publikacja)</h4>
                    {form.schema_enabled ? (
                      <Badge className="text-[10px] bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/30">
                        włączona
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">
                        wyłączona
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Używane TYLKO przy publikacji do WP (JSON-LD Person). Nie wpływa na generowanie treści w Etap 1/2.
                    {!form.schema_enabled && " Włącz przełącznik, żeby wypełnić dane autora dla schema.org."}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="flex items-center gap-2">
                  {dirty && (
                    <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30">
                      niezapisane
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    className="h-8"
                  >
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {saving ? "Zapisuję..." : "Zapisz"}
                  </Button>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wide">schema</span>
                  <Switch
                    checked={form.schema_enabled}
                    onCheckedChange={(v) => { setForm((p) => ({ ...p, schema_enabled: v })); setDirty(true); }}
                    aria-label="Włącz schema.org Person dla tego profilu"
                  />
                </div>
              </div>
            </div>

            {/* Form — disabled gdy off */}
            <fieldset
              disabled={!form.schema_enabled}
              className={`space-y-4 ${!form.schema_enabled ? "opacity-40 pointer-events-none" : ""}`}
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Slug autora (URL-safe)</FieldLabel>
                  <Input
                    value={form.author_slug}
                    onChange={(e) => updateField("author_slug", e.target.value)}
                    placeholder="np. jakub-wielbinski"
                    className="h-10 font-mono text-xs"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Auto-generowany z "Imię i nazwisko" — edytowalny.
                  </p>
                </div>
                <div>
                  <FieldLabel>Stanowisko / rola (jobTitle)</FieldLabel>
                  <Input
                    value={form.author_job_title}
                    onChange={(e) => updateField("author_job_title", e.target.value)}
                    placeholder="np. Konsultant rozwoju zawodowego"
                    className="h-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>URL strony autora na WP</FieldLabel>
                  <Input
                    value={form.author_url}
                    onChange={(e) => updateField("author_url", e.target.value)}
                    placeholder="https://twojadomena.pl/autor/jakub-wielbinski"
                    className="h-10 text-xs"
                  />
                </div>
                <div>
                  <FieldLabel>URL zdjęcia autora</FieldLabel>
                  <div className="flex items-center gap-2">
                    <Input
                      value={form.author_photo_url}
                      onChange={(e) => updateField("author_photo_url", e.target.value)}
                      placeholder="https://..."
                      className="h-10 text-xs flex-1"
                    />
                    {form.author_photo_url && (
                      <img
                        src={form.author_photo_url}
                        alt="preview"
                        className="w-10 h-10 rounded-full object-cover border border-border"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    Bio autora (HTML, max 500 znaków — {(form.author_bio_html ?? "").length}/500)
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSuggestBio}
                    disabled={suggestingBio || !form.public_author_label}
                    className="h-7 text-xs"
                  >
                    {suggestingBio ? (
                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    ) : (
                      <Wand2 className="h-3 w-3 mr-1.5" />
                    )}
                    {suggestingBio ? "Generuję..." : "AI Suggest (Claude Haiku)"}
                  </Button>
                </div>
                <Textarea
                  value={form.author_bio_html}
                  onChange={(e) => updateField("author_bio_html", e.target.value.slice(0, 500))}
                  rows={4}
                  className="resize-none text-xs"
                  placeholder="<p>2-3 zdania o autorze — zawód, perspektywa, z czym kojarzy...</p>"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    knowsAbout — obszary ekspertyzy ({form.author_knows_about.length})
                  </label>
                  {Array.isArray(profile?.core_entities) && profile.core_entities.length > 0 &&
                    form.author_knows_about.length === 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateArrayField("author_knows_about", profile.core_entities)}
                        className="h-6 text-[10px]"
                      >
                        <Sparkles className="h-2.5 w-2.5 mr-1" />
                        Użyj core_entities profilu
                      </Button>
                    )}
                </div>
                <PhraseTagList
                  phrases={form.author_knows_about} field="knowsAbout"
                  onRemove={(i) => removePhrase("author_knows_about", i)}
                  onAdd={(v) => addPhrase("author_knows_about", v)}
                  placeholder="Dodaj temat + Enter"
                  variant="secondary"
                />
              </div>

              <div>
                <FieldLabel>Profile społeczne (Person.sameAs)</FieldLabel>
                <SocialProfilesEditor
                  platforms={AUTHOR_PLATFORMS}
                  slug={form.author_slug || slugify(form.public_author_label) || "autor"}
                  value={form.author_same_as}
                  onChange={(urls) => updateArrayField("author_same_as", urls)}
                  scopeLabel="autora"
                />
              </div>
            </fieldset>
          </div>
          )}
        </>
      )}
    </div>
  );
}
